import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { brandService, DEFAULT_BRAND, type Brand } from '@/services/brand';
import { useBrand } from '@/store/useBrand';

import styles from './ConfigMarca.module.css';

export function ConfigMarca() {
  const current = useBrand((s) => s.brand);
  const applyBrand = useBrand((s) => s.applyBrand);
  const [form, setForm] = useState<Brand>(current);
  const [ok, setOk] = useState(false);

  useEffect(() => setForm(current), [current]);

  const set = <K extends keyof Brand>(k: K, v: Brand[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setOk(false);
  };

  const save = useMutation({
    mutationFn: () => brandService.update(form),
    onSuccess: (brand) => {
      applyBrand(brand); // efeito imediato em runtime
      setOk(true);
    },
  });

  const onLogoFile = (file: File | undefined, field: 'logo_url' | 'favicon') => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set(field, String(reader.result)); // data URI
    reader.readAsDataURL(file);
  };

  const previewColor = form.brand_color || 'var(--nexus-brand)';

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Marca</h1>
          <p className={styles.subtitle}>
            White-label em runtime. O que você definir aqui aplica na hora — sem
            rebuild, sem código. Cliente novo = preencher esta tela.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="ghost"
            onClick={() => setForm({ ...DEFAULT_BRAND })}
          >
            Restaurar padrão
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Salvar
          </Button>
        </div>
      </header>

      {ok && <p className={styles.ok}>Marca aplicada.</p>}
      {save.isError && <p className={styles.err}>Falha ao salvar (precisa ser admin).</p>}

      <div className={styles.grid}>
        <div className={styles.formCol}>
          <Field label="Nome" hint="Aparece no header e no login">
            <input className={styles.input} value={form.name}
              onChange={(e) => set('name', e.target.value)} maxLength={40} />
          </Field>
          <Field label="Sufixo" hint="Ex.: 'OS', 'Desk' (opcional)">
            <input className={styles.input} value={form.sub}
              onChange={(e) => set('sub', e.target.value)} maxLength={12} />
          </Field>
          <Field label="Tagline" hint="Linha sob o nome no login">
            <input className={styles.input} value={form.tagline}
              onChange={(e) => set('tagline', e.target.value)} maxLength={120} />
          </Field>
          <Field label="Cor da marca" hint="Vazio = azul padrão WIREDESK">
            <div className={styles.colorRow}>
              <input type="color" className={styles.colorPick}
                value={form.brand_color || '#3b82f6'}
                onChange={(e) => set('brand_color', e.target.value)} />
              <input className={styles.input} placeholder="#RRGGBB"
                value={form.brand_color}
                onChange={(e) => set('brand_color', e.target.value)} maxLength={9} />
              {form.brand_color && (
                <button type="button" className={styles.clearBtn}
                  onClick={() => set('brand_color', '')}>limpar</button>
              )}
            </div>
          </Field>
          <Field label="Logo" hint="PNG/SVG. Vazio = wordmark do nome">
            <div className={styles.fileRow}>
              <input type="file" accept="image/*"
                onChange={(e) => onLogoFile(e.target.files?.[0], 'logo_url')} />
              {form.logo_url && (
                <button type="button" className={styles.clearBtn}
                  onClick={() => set('logo_url', '')}>remover</button>
              )}
            </div>
          </Field>
          <Field label="Favicon" hint="Opcional. Vazio = padrão">
            <div className={styles.fileRow}>
              <input type="file" accept="image/*"
                onChange={(e) => onLogoFile(e.target.files?.[0], 'favicon')} />
              {form.favicon && (
                <button type="button" className={styles.clearBtn}
                  onClick={() => set('favicon', '')}>remover</button>
              )}
            </div>
          </Field>
        </div>

        {/* Preview ao vivo */}
        <div className={styles.previewCol}>
          <p className={styles.previewLabel}>Pré-visualização</p>
          <div className={styles.previewCard}>
            <div className={styles.previewHeader}>
              {form.logo_url ? (
                <img className={styles.previewLogo} src={form.logo_url} alt="" />
              ) : (
                <span className={styles.previewMark}
                  style={{ background: previewColor }} />
              )}
              <span className={styles.previewName}>{form.name || 'NOME'}</span>
              <span className={styles.previewSub}>{form.sub}</span>
            </div>
            <p className={styles.previewTag}>{form.tagline}</p>
            <button className={styles.previewBtn}
              style={{ background: previewColor }} type="button">
              Botão de marca
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </label>
  );
}
